/**
 * useMeterList
 *
 * Derives the list of distinct meters from telemetry rows and manages the
 * selected meter state. Extracted from Dashboard and AnomalyPage to avoid
 * duplication — both pages had identical logic copy-pasted.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import type { MeterDescriptor, TelemetryRow } from '@/types';

interface UseMeterListResult {
  meters: MeterDescriptor[];
  selectedMeter: string | 'all';
  setSelectedMeter: (id: string | 'all') => void;
  /** Rows filtered to the selected meter (no time-window applied). */
  meterRows: TelemetryRow[];
}

export function useMeterList(rows: TelemetryRow[]): UseMeterListResult {
  const { selectedSheet } = useAuth();

  const meters: MeterDescriptor[] = useMemo(() => {
    if (!selectedSheet) return [];
    return [{
      deviceId:    selectedSheet.id,
      meterSerial: selectedSheet.meta?.consumerId ?? selectedSheet.sheetId,
      location:    selectedSheet.meta?.location ?? '',
      label:       selectedSheet.label,
    }];
  }, [selectedSheet]);

  const [selectedMeter, setSelectedMeter] = useState<string | 'all'>('all');

  // Reset selection when a previously selected meter disappears (e.g. source switch)
  useEffect(() => {
    if (selectedMeter !== 'all' && !meters.some((m) => m.deviceId === selectedMeter)) {
      setSelectedMeter('all');
    }
  }, [meters, selectedMeter]);

  const meterRows: TelemetryRow[] = useMemo(() => {
    return rows;
  }, [rows]);

  return { meters, selectedMeter, setSelectedMeter, meterRows };
}
